const KEY = 'omni.plugins.v1'

// Which plugins the user has installed/enabled, persisted in the browser.
// Shape: { [id]: { manifest, enabled, installedAt, disabledReason? } }
export class PluginStore {
  #state
  #listeners = new Set()

  constructor() {
    try {
      this.#state = JSON.parse(localStorage.getItem(KEY)) ?? {}
    } catch {
      this.#state = {}
    }
  }

  #save() {
    localStorage.setItem(KEY, JSON.stringify(this.#state))
    for (const cb of this.#listeners) cb()
  }

  subscribe(cb) {
    this.#listeners.add(cb)
    return { dispose: () => this.#listeners.delete(cb) }
  }

  all() {
    return this.#state
  }

  get(id) {
    return this.#state[id]
  }

  isEmpty() {
    return Object.keys(this.#state).length === 0
  }

  install(manifest, { enabled = true } = {}) {
    this.#state[manifest.id] = {
      manifest,
      enabled,
      installedAt: Date.now(),
    }
    this.#save()
  }

  updateManifest(manifest) {
    const entry = this.#state[manifest.id]
    if (!entry) return
    if (JSON.stringify(entry.manifest) !== JSON.stringify(manifest)) {
      entry.manifest = manifest
      this.#save()
    }
  }

  uninstall(id) {
    delete this.#state[id]
    this.#save()
  }

  setEnabled(id, enabled, disabledReason) {
    const entry = this.#state[id]
    if (!entry) return
    entry.enabled = enabled
    if (enabled) delete entry.disabledReason
    else if (disabledReason) entry.disabledReason = disabledReason
    this.#save()
  }

  enabledManifests() {
    return Object.values(this.#state)
      .filter((e) => e.enabled)
      .map((e) => e.manifest)
  }
}
